import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Paper, 
  TextField, 
  Box, 
  Chip, 
  IconButton, 
  CircularProgress, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import EmailMarketingService from '../../../services/EmailMarketingService';
import EmailTemplateService from '../../../services/EmailTemplateService';
import EmailListService from '../../../services/EmailListService';
import ViewEmailModal from './ViewEmailModal';
import EditEmailModal from './EditEmailModal';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import dayjs from 'dayjs';

const HistoryTab = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sentEmails, setSentEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Cargar plantillas para obtener nombres
  const loadTemplates = useCallback(async () => {
    try {
      const response = await EmailTemplateService.getTemplates(1, 1000, '');
      const templatesData = response.data || response;
      const templatesArray = Array.isArray(templatesData) ? templatesData : [];
      setTemplates(templatesArray);
    } catch (err) {
      console.error('Error loading templates for history:', err);
    }
  }, []);

  // Cargar listas de correos
  const loadLists = useCallback(async () => {
    try {
      const response = await EmailListService.getLists(1, 1000, '');
      const listsArray = response.data || [];
      setLists(listsArray);
    } catch (err) {
      console.error('Error loading lists for history:', err);
    }
  }, []);

  // Cargar historial de envíos del backend
  const loadEmailHistory = useCallback(async (page, pageSize, search) => {
    try {
      setLoading(true);
      setError(null);
      const response = await EmailMarketingService.getEmailHistory(
        page + 1, // El backend espera página 1-indexed
        pageSize,
        search || ''
      );
      
      setSentEmails(response.data || []);
      setTotalRows(response.total || 0);
    } catch (err) {
      console.error('Error loading email history:', err);
      setError(t('error_loading_email_history') || 'Error al cargar el historial de correos');
      setSentEmails([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Cargar plantillas y listas al montar
  useEffect(() => {
    loadTemplates();
    loadLists();
  }, [loadTemplates, loadLists]);

  // Cargar datos al montar y cuando cambia la paginación
  useEffect(() => {
    loadEmailHistory(paginationModel.page, paginationModel.pageSize, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [paginationModel.page, paginationModel.pageSize, loadEmailHistory]);

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      loadEmailHistory(0, paginationModel.pageSize, newSearchTerm);
    }, 500);
  };

  // Función para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
    loadEmailHistory(newModel.page, newModel.pageSize, searchTerm);
  };

  // Función helper para obtener el color del status
  const getStatusColor = (status) => {
    if (!status) return 'default';
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case 'SENT':
      case 'DONE':
      case 'SEND':
        return 'success'; // Verde
      case 'SENDING':
        return 'warning'; // Amarillo
      case 'PENDING':
        return 'default'; // Gris
      case 'FAIL':
      case 'FAILED':
      case 'ERROR':
        return 'error'; // Rojo
      default:
        return 'default'; // Gris por defecto
    }
  };

  // Función helper para obtener el label del status
  const getStatusLabel = (status) => {
    if (!status) return '-';
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case 'SENT':
      case 'DONE':
      case 'SEND':
        return t('sent') || 'Enviado';
      case 'SENDING':
        return t('sending') || 'Enviando';
      case 'PENDING':
        return t('pending') || 'Pendiente';
      case 'FAIL':
      case 'FAILED':
      case 'ERROR':
        return t('failed') || 'Fallido';
      default:
        return status || '-';
    }
  };

  // Función para ver email
  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setViewModalOpen(true);
  };

  // Función para editar email (solo si está en estado PENDING)
  const handleEditEmail = (email) => {
    setSelectedEmail(email);
    setEditModalOpen(true);
  };

  // Función para guardar cambios del email
  const handleSaveEmail = async (emailData) => {
    if (!selectedEmail || !selectedEmail.id) return;

    try {
      await EmailMarketingService.updateEmail(selectedEmail.id, emailData);
      SnackbarUtils.success(t('email_updated_successfully') || 'Correo actualizado exitosamente');
      // Recargar el historial
      loadEmailHistory(paginationModel.page, paginationModel.pageSize, searchTerm);
    } catch (err) {
      console.error('Error updating email:', err);
      SnackbarUtils.error(err.response?.data?.message || t('error_updating_email') || 'Error al actualizar el correo');
      throw err;
    }
  };

  // Verificar si el email está en estado PENDING
  const isPending = (status) => {
    return status && status.toUpperCase() === 'PENDING';
  };

  // Función para abrir el diálogo de eliminación
  const handleDeleteClick = (email) => {
    setEmailToDelete(email);
    setDeleteDialogOpen(true);
  };

  // Función para eliminar el email
  const handleDeleteEmail = async () => {
    if (!emailToDelete || !emailToDelete.id) return;

    try {
      setDeleting(true);
      await EmailMarketingService.deleteEmail(emailToDelete.id);
      SnackbarUtils.success(t('email_deleted_successfully') || 'Correo eliminado exitosamente');
      setDeleteDialogOpen(false);
      setEmailToDelete(null);
      // Recargar el historial
      loadEmailHistory(paginationModel.page, paginationModel.pageSize, searchTerm);
    } catch (err) {
      console.error('Error deleting email:', err);
      SnackbarUtils.error(err.response?.data?.message || t('error_deleting_email') || 'Error al eliminar el correo');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      field: 'subject',
      headerName: t('subject'),
      flex: 2,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'listName',
      headerName: t('list'),
      flex: 1.5,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'sentCount',
      headerName: t('sent_count') || 'Enviados',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      renderCell: (params) => {
        return params.value !== null && params.value !== undefined ? params.value : 0;
      }
    },
    {
      field: 'failedCount',
      headerName: t('failed_count') || 'Fallidos',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      renderCell: (params) => {
        return params.value !== null && params.value !== undefined ? params.value : 0;
      }
    },
    {
      field: 'scheduled',
      headerName: t('type'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value ? (t('scheduled') || 'Programado') : (t('immediate') || 'Inmediato')}
          size="small"
          color={params.value ? 'warning' : 'info'}
          variant="outlined"
        />
      )
    },
    {
      field: 'sentAt',
      headerName: t('sent_date'),
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.value) {
          // Si no hay sentAt pero hay scheduledDate, mostrar la fecha programada
          const scheduledDate = params.row.scheduledDate;
          if (scheduledDate) {
            const date = dayjs(scheduledDate);
            return (
              <Box>
                <Box>{date.format('DD/MM/YYYY HH:mm')}</Box>
                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {t('scheduled') || 'Programado'}
                </Box>
              </Box>
            );
          }
          return '-';
        }
        const date = dayjs(params.value);
        return date.format('DD/MM/YYYY HH:mm');
      }
    },
    {
      field: 'status',
      headerName: t('status'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          size="small"
          color={getStatusColor(params.value)}
        />
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isEmailPending = isPending(params.row.status);
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 0.5 }}>
            <IconButton 
              size="small" 
              title={t('view_details')}
              onClick={() => handleViewEmail(params.row)}
              sx={{
                width: 40,
                height: 40,
                minWidth: 40,
                minHeight: 40,
                maxWidth: 40,
                maxHeight: 40,
                padding: 0,
                borderRadius: '50%',
                '&:hover': {
                  borderRadius: '50%'
                }
              }}
            >
              {getIcon('eye')}
            </IconButton>
            {isEmailPending && (
              <>
                <IconButton 
                  size="small" 
                  title={t('edit')}
                  onClick={() => handleEditEmail(params.row)}
                  sx={{
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    minHeight: 40,
                    maxWidth: 40,
                    maxHeight: 40,
                    padding: 0,
                    borderRadius: '50%',
                    '&:hover': {
                      borderRadius: '50%'
                    }
                  }}
                >
                  {getIcon('edit')}
                </IconButton>
                <IconButton 
                  size="small" 
                  title={t('delete')}
                  color="error"
                  onClick={() => handleDeleteClick(params.row)}
                  sx={{
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    minHeight: 40,
                    maxWidth: 40,
                    maxHeight: 40,
                    padding: 0,
                    borderRadius: '50%',
                    '&:hover': {
                      borderRadius: '50%'
                    }
                  }}
                >
                  {getIcon('trash')}
                </IconButton>
              </>
            )}
          </Box>
        );
      }
    }
  ];

  return (
    <Paper sx={{ padding: 2, mt: 2 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <TextField
          label={t('search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ width: 300 }}
          placeholder={t('search_emails')}
        />
      </Box>

      {loading && sentEmails.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1200 }}>
            <DataGrid
              rows={sentEmails}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              pageSizeOptions={[10, 25, 50]}
              getRowId={(row) => row.id}
              autoHeight
              disableRowSelectionOnClick
              loading={loading}
              rowCount={totalRows}
              paginationMode="server"
            />
          </Box>
        </Box>
      )}

      <ViewEmailModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedEmail(null);
        }}
        email={selectedEmail}
      />

      <EditEmailModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedEmail(null);
        }}
        email={selectedEmail}
        onSave={handleSaveEmail}
        templates={templates}
        lists={lists}
      />

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setEmailToDelete(null);
        }}
      >
        <DialogTitle>{t('confirm_delete') || 'Confirmar eliminación'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_delete_email_message', { subject: emailToDelete?.subject }) || 
              `¿Estás seguro de que deseas eliminar el correo "${emailToDelete?.subject}"? Esta acción no se puede deshacer.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setEmailToDelete(null);
            }}
            variant="outlined"
            disabled={deleting}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleDeleteEmail} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HistoryTab;

